/**
 * 结构化错误。
 *
 * 约束来源：CONTRACT.md 第 17 行错误码表。code 0 只代表提交成功，
 * 不代表"已送达/已读"。错误信息经过服务端脱敏，客户端不得拼接原始响应体。
 */

export type SupportErrorKind =
  | 'capacity'
  | 'conflict'
  | 'expired-cursor'
  | 'forbidden'
  | 'internal'
  | 'invalid'
  | 'network'
  | 'not-found'
  | 'protocol'
  | 'timeout'
  | 'too-large'
  | 'unauthorized'
  | 'unknown';

export const SUPPORT_ERROR_CODES = {
  capacity: 1_901_006,
  conflict: 1_901_005,
  expiredCursor: 1_901_008,
  forbidden: 1_901_002,
  internal: 1_901_500,
  invalid: 1_901_003,
  notFound: 1_901_004,
  tooLarge: 1_901_007,
  unauthorized: 1_901_001,
} as const;

const KIND_BY_CODE = new Map<number, SupportErrorKind>([
  [SUPPORT_ERROR_CODES.unauthorized, 'unauthorized'],
  [SUPPORT_ERROR_CODES.forbidden, 'forbidden'],
  [SUPPORT_ERROR_CODES.invalid, 'invalid'],
  [SUPPORT_ERROR_CODES.notFound, 'not-found'],
  [SUPPORT_ERROR_CODES.conflict, 'conflict'],
  [SUPPORT_ERROR_CODES.capacity, 'capacity'],
  [SUPPORT_ERROR_CODES.tooLarge, 'too-large'],
  [SUPPORT_ERROR_CODES.expiredCursor, 'expired-cursor'],
  [SUPPORT_ERROR_CODES.internal, 'internal'],
]);

/** 可通过重新同步恢复的错误（并发冲突、游标过期、容量暂时占满）。 */
const RECOVERABLE: ReadonlySet<SupportErrorKind> = new Set<SupportErrorKind>([
  'capacity',
  'conflict',
  'expired-cursor',
]);

export function kindForCode(code: number): SupportErrorKind {
  return KIND_BY_CODE.get(code) ?? 'unknown';
}

export interface SupportErrorInit {
  code: number;
  kind?: SupportErrorKind;
  message: string;
  requestId?: string;
}

export class SupportError extends Error {
  readonly code: number;

  readonly kind: SupportErrorKind;

  readonly requestId?: string;

  constructor(init: SupportErrorInit) {
    super(init.message);
    this.name = 'SupportError';
    this.code = init.code;
    this.kind = init.kind ?? kindForCode(init.code);
    if (init.requestId !== undefined) this.requestId = init.requestId;
  }

  /** 是否属于"重新同步/重试即可"的可恢复错误。 */
  get recoverable(): boolean {
    return RECOVERABLE.has(this.kind);
  }

  /** 是否需要重新认证。 */
  get needsReauth(): boolean {
    return this.kind === 'unauthorized';
  }
}

export function isSupportError(value: unknown): value is SupportError {
  return value instanceof SupportError;
}

/** 网络/超时这类传输层错误也统一成 SupportError，避免调用方到处判断。 */
export function transportError(
  kind: 'network' | 'timeout',
  message: string,
): SupportError {
  return new SupportError({ code: 0, kind, message });
}